import { create } from "zustand";
import { dossiersApi } from "@services/api/dossiers";
import type { Dossier } from "types/dossier";

const PAGE_SIZE = 25;

/** Get a dossier by ID from the store */
export function getDossierById(id: string): Dossier | undefined {
  return useDossierStore.getState().dossiers.find((d) => d.id === id);
}

interface DossierStore {
  dossiers: Dossier[];
  isLoading: boolean;
  isRefreshing: boolean;
  hasMore: boolean;
  hasPrev: boolean;
  page: number;
  typeFilter: string;
  sortBy: string;
  sortDir: string;
  error: string | null;
  _fetchedOrgId: string | undefined;

  fetchDossiers: (orgId: string, type?: string) => Promise<void>;
  fetchNextPage: (orgId: string) => Promise<void>;
  fetchPrevPage: (orgId: string) => Promise<void>;
  setSort: (orgId: string, sortBy: string, sortDir: string) => Promise<void>;
  addDossier: (dossier: Dossier) => void;
  removeDossiers: (ids: string[]) => void;
}

export const useDossierStore = create<DossierStore>((set) => ({
  dossiers: [],
  isLoading: false,
  isRefreshing: false,
  hasMore: false,
  hasPrev: false,
  page: 0,
  typeFilter: "",
  sortBy: "",
  sortDir: "asc",
  error: null,
  _fetchedOrgId: undefined,

  fetchDossiers: async (orgId: string, type?: string) => {
    const normalizedType = type === "all" ? undefined : type;
    const state = useDossierStore.getState();
    if (state.isLoading || state.isRefreshing) return;
    const showLoading = state.dossiers.length === 0;
    set({ isLoading: showLoading, isRefreshing: true, error: null, page: 0, _fetchedOrgId: orgId, typeFilter: normalizedType ?? "" });
    try {
      const { sortBy, sortDir } = useDossierStore.getState();
      const dossiers = await dossiersApi.list({
        org_id: orgId,
        type: normalizedType,
        limit: PAGE_SIZE,
        offset: 0,
        sort_by: sortBy || undefined,
        sort_dir: sortDir,
      });
      set({
        dossiers,
        isLoading: false,
        isRefreshing: false,
        page: 1,
        hasMore: dossiers.length === PAGE_SIZE,
        hasPrev: false,
      });
    } catch (error: any) {
      console.error("Error fetching dossiers:", error);
      set({ dossiers: [], isLoading: false, isRefreshing: false, _fetchedOrgId: undefined, error: "Échec du chargement des dossiers" });
    }
  },

  fetchNextPage: async (orgId: string) => {
    const { isLoading, hasMore, page, typeFilter, sortBy, sortDir } = useDossierStore.getState();
    if (isLoading || !hasMore) return;
    set({ isLoading: true });
    try {
      const next = await dossiersApi.list({
        org_id: orgId,
        type: typeFilter || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        sort_by: sortBy || undefined,
        sort_dir: sortDir,
      });
      set((state) => ({
        dossiers: next,
        isLoading: false,
        page: state.page + 1,
        hasMore: next.length === PAGE_SIZE,
        hasPrev: true,
      }));
    } catch (error: any) {
      console.error("Error fetching next page:", error);
      set({ isLoading: false });
    }
  },

  fetchPrevPage: async (orgId: string) => {
    const { isLoading, hasPrev, page, typeFilter, sortBy, sortDir } = useDossierStore.getState();
    if (isLoading || !hasPrev) return;
    const prevPage = page - 2;
    set({ isLoading: true });
    try {
      const prev = await dossiersApi.list({
        org_id: orgId,
        type: typeFilter || undefined,
        limit: PAGE_SIZE,
        offset: prevPage * PAGE_SIZE,
        sort_by: sortBy || undefined,
        sort_dir: sortDir,
      });
      set((state) => ({
        dossiers: prev,
        isLoading: false,
        page: state.page - 1,
        hasMore: true,
        hasPrev: prevPage > 0,
      }));
    } catch (error: any) {
      console.error("Error fetching prev page:", error);
      set({ isLoading: false });
    }
  },

  setSort: async (orgId: string, sortBy: string, sortDir: string) => {
    const { typeFilter } = useDossierStore.getState();
    set({ sortBy, sortDir, _fetchedOrgId: undefined, isLoading: true, page: 0, hasPrev: false });
    try {
      const dossiers = await dossiersApi.list({
        org_id: orgId,
        type: typeFilter || undefined,
        limit: PAGE_SIZE,
        offset: 0,
        sort_by: sortBy || undefined,
        sort_dir: sortDir,
      });
      set({ dossiers, isLoading: false, page: 1, hasMore: dossiers.length === PAGE_SIZE, hasPrev: false });
    } catch (error: any) {
      set({ isLoading: false });
    }
  },

  addDossier: (dossier: Dossier) => {
    set((state) => ({ dossiers: [dossier, ...state.dossiers], _fetchedOrgId: undefined }));
  },

  removeDossiers: (ids: string[]) => {
    set((state) => ({
      dossiers: state.dossiers.filter((d) => !ids.includes(d.id)),
    }));
    ids.forEach((id) => {
      dossiersApi.delete(id).catch((error) => {
        console.error("Error deleting dossier from API:", error);
      });
    });
  },
}));
